import { APIError } from "./errors";
import { makeQueryString } from "./utils";
import camelCase from "lodash/camelCase";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import isPlainObject from "lodash/isPlainObject";
import omitBy from "lodash/omitBy";
import transform from "lodash/transform";
import words from "lodash/words";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const compareArrays = (first, second) => {
  return JSON.stringify(first) === JSON.stringify(second);
};

const deepKeyUpdate = (obj, func, handlers = {}, casingIgnorePaths = []) => {
  if (
    compareArrays(casingIgnorePaths, ["*"]) ||
    (!isObject(obj) && !isArray(obj))
  ) {
    return obj;
  }

  return transform(obj, (acc, value, key) => {
    // do not convert case for the key if it is a UUID, but continue processing children
    const modifiedKey = UUID_RE.test(key) ? key : func(key);

    if (
      casingIgnorePaths?.length > 0 &&
      (casingIgnorePaths.includes(modifiedKey) ||
        casingIgnorePaths.includes(key))
    ) {
      acc[modifiedKey] = value;
    } else if (isPlainObject(value)) {
      acc[modifiedKey] = deepKeyUpdate(
        value,
        func,
        handlers,
        casingIgnorePaths,
      );
    } else if (isArray(value)) {
      acc[modifiedKey] = value.map((item) =>
        deepKeyUpdate(item, func, handlers, casingIgnorePaths),
      );
    } else {
      acc[modifiedKey] = value;
    }
  });
};

export const deepCamelCaseKeys = (obj, casingIgnorePaths) =>
  deepKeyUpdate(obj, camelCase, {}, casingIgnorePaths);

const snakeCase = (text) =>
  words(text).reduce(
    (result, word, index) =>
      result + (index > 0 ? "_" : "") + word.toLowerCase(),
    "",
  );

const deepSnakeCase = (obj, casingIgnorePaths) =>
  deepKeyUpdate(obj, snakeCase, {}, casingIgnorePaths);

const makeUrl = (url, params, settings) => {
  const path = settings.trailingSlash && !url.endsWith("/") ? `${url}/` : url;
  const fullUrl = `${settings.scheme}${settings.domain}/${settings.basePath}/${path}`;

  return `${fullUrl}${makeQueryString(params, settings.hasMultipleQueryParamValues || false)}`;
};

const CSRF_COOKIE_NAME = "csrftoken";
const CSRF_HEADER_NAME = "X-CSRFToken";

const getCsrfToken = () => {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(
    new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`),
  );

  return match ? decodeURIComponent(match[1]) : null;
};

const call = (
  resource,
  params,
  apiSettings,
  requestOptions,
  casingIgnorePaths,
) => {
  // Filter out headers that have null or undefined values
  // using == 'null' here to catch 'null' and 'undefined' but not empty strings
  const settingsHeaders = omitBy(apiSettings.headers, (value) => value == null);
  const csrfToken = getCsrfToken();
  const method = requestOptions.method.toUpperCase();
  const hasBody = requestOptions.data !== undefined;

  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
    ...settingsHeaders,
  };

  const url = makeUrl(resource, params, apiSettings);

  return fetch(url, {
    body: hasBody ? JSON.stringify(requestOptions.data) : undefined,
    credentials: "include",
    headers,
    method,
  }).then(async (response) => {
    if (response.ok && requestOptions.responseType === "text") {
      return response.text();
    }

    const rawBody =
      response.status !== 204 ? await response.json().catch(() => ({})) : {};
    const data = rawBody ? deepCamelCaseKeys(rawBody, casingIgnorePaths) : {};

    if (!response.ok) {
      const _response = {
        body: data,
        headers: Object.fromEntries(response.headers.entries()),
        status: response.status,
        url,
      };

      const _request = {
        body: params,
        headers,
      };

      throw new APIError(response.statusText, _response, _request);
    }

    if (
      apiSettings.useHeaderPagination &&
      response.headers.has("x-total-count")
    ) {
      return {
        data,
        totalCount: Number(response.headers.get("x-total-count")),
      };
    }

    if (apiSettings.returnCookies) {
      return {
        cookies: response.headers.get("set-cookie"),
        data,
      };
    }

    return data;
  });
};

const get = (resource, params, settings, casingIgnorePaths) =>
  call(
    resource,
    params,
    settings,
    {
      method: "get",
    },
    casingIgnorePaths,
  );

const post = (resource, data, settings, casingIgnorePaths) => {
  const options = {
    data: deepSnakeCase(data, casingIgnorePaths),
    method: "post",
  };

  return call(resource, null, settings, options, casingIgnorePaths);
};

const postText = (resource, data, settings, casingIgnorePaths) => {
  const options = {
    data: deepSnakeCase(data, casingIgnorePaths),
    method: "post",
    responseType: "text",
  };

  return call(resource, null, settings, options, casingIgnorePaths);
};

const patch = (resource, data, settings, casingIgnorePaths) => {
  const options = {
    data: deepSnakeCase(data, casingIgnorePaths),
    method: "patch",
  };

  return call(resource, null, settings, options, casingIgnorePaths);
};

const put = (resource, data, settings, casingIgnorePaths) => {
  const options = {
    data: deepSnakeCase(data, casingIgnorePaths),
    method: "put",
  };

  return call(resource, null, settings, options, casingIgnorePaths);
};

const _delete = (resource, data, settings) => {
  const options = {
    data: deepSnakeCase(data),
    method: "delete",
  };

  return call(resource, null, settings, options);
};

const options = (resource, settings) => {
  const options = {
    method: "options",
  };

  return call(resource, null, settings, options);
};

export default {
  delete: _delete,
  get,
  makeUrl,
  options,
  patch,
  post,
  postText,
  put,
};
